import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminService, CreateCountyData, UpdateCountyData } from '../services/superadmin.service';

// Dashboard hooks
export const useSuperAdminDashboard = (params?: { date_filter?: string; start_date?: string; end_date?: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'dashboard', params],
    queryFn: () => superAdminService.getDashboardStats(params),
  });

  return {
    stats: data?.data,
    isLoading,
    error,
  };
};

export const useRevenueChart = (params?: { monthly_filter?: string; start_date?: string; end_date?: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'revenue-chart', params],
    queryFn: () => superAdminService.getRevenueChart(params),
  });

  return {
    chartData: data?.data,
    isLoading,
    error,
  };
};

export const useCountyPerformance = (params?: { date_filter?: string; start_date?: string; end_date?: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county-performance', params],
    queryFn: () => superAdminService.getCountyPerformance(params),
  });

  return {
    performance: data?.data,
    isLoading,
    error,
  };
};

export const useRecentTransactions = (params?: { limit?: number }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'recent-transactions', params],
    queryFn: () => superAdminService.getRecentTransactions(params),
  });

  return {
    transactions: data?.data,
    isLoading,
    error,
  };
};

// County management hooks
export const useCounties = (page?: number, perPage?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'counties', page, perPage],
    queryFn: () => superAdminService.getCounties(page, perPage),
  });

  return {
    counties: data?.data,
    isLoading,
    error,
  };
};

export const useCounty = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county', id],
    queryFn: () => superAdminService.getCounty(id),
    enabled: !!id,
  });

  return {
    county: data?.data,
    isLoading,
    error,
  };
};

export const useCreateCounty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCountyData) => superAdminService.createCounty(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'counties'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'dashboard'] });
    },
  });
};

export const useUpdateCounty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCountyData }) =>
      superAdminService.updateCounty(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'counties'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county', variables.id] });
    },
  });
};

export const useDeleteCounty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteCounty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'counties'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'dashboard'] });
    },
  });
};

export const useCountyStats = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county', id, 'stats'],
    queryFn: () => superAdminService.getCountyStats(id),
    enabled: !!id,
  });

  return {
    stats: data?.data,
    isLoading,
    error,
  };
};

export const useCountyUsers = (countyId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county', countyId, 'users'],
    queryFn: () => superAdminService.getCountyUsers(countyId),
    enabled: !!countyId,
  });

  // Handle paginated response structure
  const responseData = data?.data as any;
  const users = Array.isArray(responseData) ? responseData : responseData?.data;

  return {
    users,
    isLoading,
    error,
  };
};

export const useCountyModules = (countyId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county', countyId, 'modules'],
    queryFn: () => superAdminService.getCountyModules(countyId),
    enabled: !!countyId,
  });

  return {
    modules: data?.data,
    isLoading,
    error,
  };
};

export const useSuspendCountyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ countyId, userId }: { countyId: number; userId: number }) =>
      superAdminService.suspendCountyUser(countyId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county', variables.countyId, 'users'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-users'] });
    },
  });
};

export const useUnsuspendCountyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ countyId, userId }: { countyId: number; userId: number }) =>
      superAdminService.unsuspendCountyUser(countyId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county', variables.countyId, 'users'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-users'] });
    },
  });
};

// County users management hooks
export const useCountyUsersList = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county-users', page],
    queryFn: () => superAdminService.getCountyUsersList(page),
  });

  return {
    users: data?.data,
    isLoading,
    error,
  };
};

export const useCountyUser = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county-user', id],
    queryFn: () => superAdminService.getCountyUser(id),
    enabled: !!id,
  });

  return {
    user: data?.data,
    isLoading,
    error,
  };
};

export const useCreateCountyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createCountyUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-users'] });
    },
  });
};

export const useUpdateCountyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateCountyUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-users'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-user', variables.id] });
    },
  });
};

export const useDeleteCountyUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteCountyUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'county-users'] });
    },
  });
};

// Roles & Permissions hooks
export const useCountyRoles = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county-roles'],
    queryFn: () => superAdminService.getCountyRoles(),
  });

  return {
    roles: data?.data,
    isLoading,
    error,
  };
};

export const useCountyPermissions = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'county-permissions'],
    queryFn: () => superAdminService.getCountyPermissions(),
  });

  return {
    permissions: data?.data,
    isLoading,
    error,
  };
};

// Touch Points hooks (Superadmin)
export const useSuperAdminTouchPoints = (page?: number, perPage?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'touch-points', page, perPage],
    queryFn: () => superAdminService.getTouchPoints(page, perPage),
  });

  return {
    touchPoints: data?.data,
    isLoading,
    error,
  };
};

export const useTouchPoint = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'touch-point', id],
    queryFn: () => superAdminService.getTouchPoint(id),
    enabled: !!id,
  });

  return {
    touchPoint: data?.data,
    isLoading,
    error,
  };
};

export const useCreateTouchPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createTouchPoint(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
    },
  });
};

export const useUpdateTouchPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateTouchPoint(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-point', variables.id] });
    },
  });
};

export const useDeleteTouchPoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteTouchPoint(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
    },
  });
};

export const useRegenerateTouchPointKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.regenerateTouchPointKey(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-point', id] });
    },
  });
};

// Superadmin Users hooks
export const useSuperadminUsers = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'superadmin-users', page],
    queryFn: () => superAdminService.getSuperadminUsers(page),
  });

  return {
    users: data?.data,
    isLoading,
    error,
  };
};

export const useSuperadminUser = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'superadmin-user', id],
    queryFn: () => superAdminService.getSuperadminUser(id),
    enabled: !!id,
  });

  return {
    user: data?.data,
    isLoading,
    error,
  };
};

export const useCreateSuperadminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createSuperadminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
    },
  });
};

export const useUpdateSuperadminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateSuperadminUser(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-user', variables.id] });
    },
  });
};

export const useDeleteSuperadminUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteSuperadminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
    },
  });
};

export const useToggleSuperadminUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.toggleSuperadminUserStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
    },
  });
};

// Superadmin Roles & Permissions
export const useSuperadminRoles = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'superadmin-roles'],
    queryFn: () => superAdminService.getSuperadminRoles(),
  });

  // Handle both paginated and non-paginated responses
  const responseData = data?.data as any;
  const roles = Array.isArray(responseData) ? responseData : (responseData?.data || []);

  return {
    roles,
    isLoading,
    error,
  };
};

export const useSuperadminRole = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'role', id],
    queryFn: () => superAdminService.getSuperadminRole(id),
    enabled: !!id,
  });

  return {
    role: data?.data,
    isLoading,
    error,
  };
};

export const useCreateSuperadminRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; guard_name?: string; permissions?: number[] }) =>
      superAdminService.createSuperadminRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
    },
  });
};

export const useUpdateSuperadminRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; guard_name?: string; permissions?: number[] } }) =>
      superAdminService.updateSuperadminRole(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'role', variables.id] });
    },
  });
};

export const useDeleteSuperadminRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteSuperadminRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
    },
  });
};

export const useSuperadminPermissions = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'superadmin-permissions'],
    queryFn: () => superAdminService.getSuperadminPermissions(),
  });

  // Handle both paginated and non-paginated responses
  const responseData = data?.data as any;
  const permissions = Array.isArray(responseData) ? responseData : (responseData?.data || []);

  return {
    permissions,
    isLoading,
    error,
  };
};

export const useSuperadminPermission = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'permission', id],
    queryFn: () => superAdminService.getSuperadminPermission(id),
    enabled: !!id,
  });

  return {
    permission: data?.data,
    isLoading,
    error,
  };
};

export const useCreateSuperadminPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; guard_name?: string }) =>
      superAdminService.createSuperadminPermission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-permissions'] });
    },
  });
};

export const useUpdateSuperadminPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; guard_name?: string } }) =>
      superAdminService.updateSuperadminPermission(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'permission', variables.id] });
    },
  });
};

export const useDeleteSuperadminPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteSuperadminPermission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-permissions'] });
    },
  });
};

// Payment Modules hooks
export const usePaymentModules = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'payment-modules', page],
    queryFn: () => superAdminService.getPaymentModules(page),
  });

  return {
    paymentModules: data?.data,
    isLoading,
    error,
  };
};

export const usePaymentModule = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'payment-module', id],
    queryFn: () => superAdminService.getPaymentModule(id),
    enabled: !!id,
  });

  return {
    paymentModule: data?.data,
    isLoading,
    error,
  };
};

export const useCreatePaymentModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createPaymentModule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
    },
  });
};

export const useUpdatePaymentModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updatePaymentModule(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-module', variables.id] });
    },
  });
};

export const useDeletePaymentModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deletePaymentModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
    },
  });
};

// Transactions hooks
export const useTransactions = (page?: number, params?: any) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'transactions', page, params],
    queryFn: () => superAdminService.getTransactions(page, params),
  });

  return {
    transactions: data?.data,
    isLoading,
    error,
  };
};

export const useTransaction = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'transaction', id],
    queryFn: () => superAdminService.getTransaction(id),
    enabled: !!id,
  });

  return {
    transaction: data?.data,
    isLoading,
    error,
  };
};

export const useTransactionStats = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'transaction-stats'],
    queryFn: () => superAdminService.getTransactionStats(),
  });

  return {
    stats: data?.data,
    isLoading,
    error,
  };
};

export const useSuccessfulTransactions = (page?: number, params?: any) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'transactions', 'successful', page, params],
    queryFn: () => superAdminService.getSuccessfulTransactions(page, params),
  });

  return {
    transactions: data?.data,
    isLoading,
    error,
  };
};

export const useFailedTransactions = (page?: number, params?: any) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'transactions', 'failed', page, params],
    queryFn: () => superAdminService.getFailedTransactions(page, params),
  });

  return {
    transactions: data?.data,
    isLoading,
    error,
  };
};

// Modules hooks
export const useModules = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'modules', page],
    queryFn: () => superAdminService.getModules(page),
  });

  return {
    modules: data?.data,
    isLoading,
    error,
  };
};

export const useModule = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'module', id],
    queryFn: () => superAdminService.getModule(id),
    enabled: !!id,
  });

  return {
    module: data?.data,
    isLoading,
    error,
  };
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createModule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'modules'] });
    },
  });
};

export const useUpdateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateModule(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'modules'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module', variables.id] });
    },
  });
};

export const useDeleteModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'modules'] });
    },
  });
};

// Schedules hooks
export const useSchedules = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'schedules', page],
    queryFn: () => superAdminService.getSchedules(page),
  });

  return {
    schedules: data?.data,
    isLoading,
    error,
  };
};

export const useSchedule = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'schedule', id],
    queryFn: () => superAdminService.getSchedule(id),
    enabled: !!id,
  });

  return {
    schedule: data?.data,
    isLoading,
    error,
  };
};

export const useCreateSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'schedules'] });
    },
  });
};

export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateSchedule(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'schedules'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'schedule', variables.id] });
    },
  });
};

export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'schedules'] });
    },
  });
};

// Parts hooks
export const useParts = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'parts', page],
    queryFn: () => superAdminService.getParts(page),
  });

  return {
    parts: data?.data,
    isLoading,
    error,
  };
};

export const usePart = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'part', id],
    queryFn: () => superAdminService.getPart(id),
    enabled: !!id,
  });

  return {
    part: data?.data,
    isLoading,
    error,
  };
};

export const useCreatePart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createPart(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'parts'] });
    },
  });
};

export const useUpdatePart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updatePart(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'parts'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'part', variables.id] });
    },
  });
};

export const useDeletePart = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deletePart(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'parts'] });
    },
  });
};

// Categories hooks
export const useCategories = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'categories', page],
    queryFn: () => superAdminService.getCategories(page),
  });

  return {
    categories: data?.data,
    isLoading,
    error,
  };
};

export const useCategory = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'category', id],
    queryFn: () => superAdminService.getCategory(id),
    enabled: !!id,
  });

  return {
    category: data?.data,
    isLoading,
    error,
  };
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'categories'] });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateCategory(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'category', variables.id] });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'categories'] });
    },
  });
};

// Services hooks
export const useServices = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'services', page],
    queryFn: () => superAdminService.getServices(page),
  });

  return {
    services: data?.data,
    isLoading,
    error,
  };
};

export const useService = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'service', id],
    queryFn: () => superAdminService.getService(id),
    enabled: !!id,
  });

  return {
    service: data?.data,
    isLoading,
    error,
  };
};

export const useCreateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'services'] });
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateService(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'services'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'service', variables.id] });
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'services'] });
    },
  });
};

// Module Types hooks
export const useModuleTypes = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'module-types', page],
    queryFn: () => superAdminService.getModuleTypes(page),
  });

  return {
    moduleTypes: data?.data,
    isLoading,
    error,
  };
};

export const useModuleType = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'module-type', id],
    queryFn: () => superAdminService.getModuleType(id),
    enabled: !!id,
  });

  return {
    moduleType: data?.data,
    isLoading,
    error,
  };
};

export const useCreateModuleType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createModuleType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
    },
  });
};

export const useUpdateModuleType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateModuleType(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-type', variables.id] });
    },
  });
};

export const useDeleteModuleType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteModuleType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
    },
  });
};

// Notifications hooks
export const useSuperadminNotifications = (page?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'notifications', page],
    queryFn: () => superAdminService.getNotifications(page),
  });

  return {
    notifications: data?.data,
    isLoading,
    error,
  };
};

export const useSuperadminNotification = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'notification', id],
    queryFn: () => superAdminService.getNotification(id),
    enabled: !!id,
  });

  return {
    notification: data?.data,
    isLoading,
    error,
  };
};

export const useCreateSuperadminNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => superAdminService.createNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
    },
  });
};

export const useUpdateSuperadminNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      superAdminService.updateNotification(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'notification', variables.id] });
    },
  });
};

export const useDeleteSuperadminNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
    },
  });
};

export const useSendNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => superAdminService.sendNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'notifications'] });
    },
  });
};

// Reports hooks
export const useSuperadminRevenueReport = (params?: { date_filter?: string; start_date?: string; end_date?: string; county_id?: number }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'reports', 'revenue', params],
    queryFn: () => superAdminService.getRevenueReport(params),
  });

  return {
    report: data?.data,
    isLoading,
    error,
  };
};

export const useSuperadminTransactionReport = (params?: { date_filter?: string; start_date?: string; end_date?: string; status?: string; county_id?: number }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'reports', 'transactions', params],
    queryFn: () => superAdminService.getTransactionReport(params),
  });

  return {
    report: data?.data,
    isLoading,
    error,
  };
};

export const useSuperadminServiceUsageReport = (params?: { date_filter?: string; start_date?: string; end_date?: string; county_id?: number }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'reports', 'service-usage', params],
    queryFn: () => superAdminService.getServiceUsageReport(params),
  });

  return {
    report: data?.data,
    isLoading,
    error,
  };
};

export const useSuperadminAuditTrailReport = (page?: number, filters?: { county_id?: number; user_id?: number; action?: string; date_from?: string; date_to?: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'reports', 'audit-trail', page, filters],
    queryFn: () => superAdminService.getAuditTrailReport(page, filters),
  });

  return {
    report: data?.data,
    isLoading,
    error,
  };
};
